"use client";

import * as React from "react";
import clsx from "clsx";
import Image from "next/image";
// 1. IMPORTUJEMY LINK
import Link from "next/link";
import TextLinkButton from "@/components/ui/TextLinkButton";
import CtaButton from "./ui/CtaButton";
import Breadcrumbs, { type BreadcrumbItem } from "./Breadcrumbs";
import Underline from "./ui/underline";
import { LazyMotion, domAnimation, m, type MotionProps } from "framer-motion";

export type PreparedImg = { src: string; blur: string } | null;

export type PreparedAttractionsCard = {
    key: string;
    slug: string;
    name: string;
    description: string;
    image: PreparedImg;
    index: number;
};

export type PreparedAttractionsFeatured = {
    slug: string;
    name: string;
    description: string;
    image: PreparedImg;
} | null;

export type PreparedAttractionsAllData = {
    heading: string;
    description: string;
    featured: PreparedAttractionsFeatured;
    cards: PreparedAttractionsCard[];
};

type BaseProps = {
    prepared: PreparedAttractionsAllData;
    breadcrumbs?: BreadcrumbItem[];
    basePath?: string;
    ctaLabel?: string;
};

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeSectionHtmlProps = Omit<
    React.ComponentPropsWithoutRef<"section">,
    keyof BaseProps | keyof MotionProps | ConflictingHtmlEvents
>;

export type AttractionsAllSectionClientProps = BaseProps & SafeSectionHtmlProps & MotionProps;

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

const reveal = {
    hidden: { clipPath: "inset(0% 0% 100% 0%)" },
    visible: {
        clipPath: "inset(0% 0% 0% 0%)",
        transition: { duration: 1.2, ease: [0.45, 0, 0.55, 1], delay: 0.2 },
    },
} as const;

export default function AttractionsAllSectionClient({
                                                        prepared,
                                                        breadcrumbs,
                                                        basePath = "/atrakcje",
                                                        ctaLabel = "NAPISZ DO NAS",
                                                        className,
                                                        ...rest
                                                    }: AttractionsAllSectionClientProps) {
    const { heading, description, featured, cards } = prepared;

    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="relume"
                initial="hidden"
                animate="visible"
                className={clsx("px-[5%] py-16 md:py-24 lg:py-28 mt-8 md:mt-16", className)}
                {...rest}
            >
                <div className="container">
                    {/* HERO */}
                    <div className="mb-12 w-full max-w-[54rem] md:mb-16 lg:mb-20">
                        {breadcrumbs && breadcrumbs.length > 0 && (
                            <m.div
                                variants={slideUp}
                                custom={0}
                                className="relative inline-block will-change-transform mb-12 md:mb-16"
                            >
                                <Breadcrumbs items={breadcrumbs} />
                                <Underline
                                    delay={0.6}
                                    useWhileInView
                                    className="pointer-events-none absolute -left-8 top-full mt-2 text-accent h-5 w-[170px] md:h-6 md:w-[230px]"
                                />
                            </m.div>
                        )}
                        <m.h1 variants={slideUp} custom={breadcrumbs && breadcrumbs.length > 0 ? 1 : 0} className="mb-5 text-4xl font-medium md:mb-6 md:text-7xl">
                            {heading}
                        </m.h1>

                        <m.p variants={slideUp} custom={breadcrumbs && breadcrumbs.length > 0 ? 2 : 1} className="md:text-md">
                            {description}
                        </m.p>

                        <m.div variants={slideUp} custom={breadcrumbs && breadcrumbs.length > 0 ? 3 : 2} className="mt-6 flex flex-wrap items-center gap-4 md:mt-8">
                            <CtaButton />
                        </m.div>
                    </div>

                    {/* WYRÓŻNIONA ATRAKCJA */}
                    {featured?.slug && (
                        <div className="mb-12 rounded-3xl md:mb-16">
                            <div className="grid grid-cols-1 items-center rounded-[40px] p-2 md:grid-cols-2 md:p-4 text-primary bg-secondary">
                                {/* ZAMIANA m.a na Link + m.div w środku */}
                                <Link
                                    href={`${basePath}/${featured.slug}`}
                                    scroll={true}
                                    className="group size-full block overflow-hidden rounded-[32px]"
                                >
                                    <m.div
                                        variants={reveal}
                                        className="size-full h-full w-full block"
                                    >
                                        {featured.image?.src && (
                                            <Image
                                                src={featured.image.src}
                                                alt={featured.name || ""}
                                                width={1200}
                                                height={900}
                                                sizes="(max-width: 768px) 100vw, 50vw"
                                                loading="lazy"
                                                decoding="async"
                                                placeholder={featured.image.blur ? "blur" : "empty"}
                                                blurDataURL={featured.image.blur}
                                                className="aspect-[4/3] size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                                            />
                                        )}
                                    </m.div>
                                </Link>

                                <div className="flex h-full flex-col items-center justify-center px-2 pt-6 pb-6 md:px-8 md:pt-24 md:pb-0">
                                    <m.div variants={slideUp} custom={breadcrumbs && breadcrumbs.length > 0 ? 4 : 3}>
                                        <h2 className="mb-4 text-3xl font-medium md:text-4xl">{featured.name}</h2>
                                        <p className="md:text-md">{featured.description}</p>
                                    </m.div>

                                    <m.div variants={slideUp} custom={breadcrumbs && breadcrumbs.length > 0 ? 5 : 4} className="mt-6 sm:mt-12 md:mt-24 self-end mr-4">
                                        <TextLinkButton
                                            text="Pokaż szczegóły"
                                            hoverColor="#803320"
                                            primaryColor="#FAF6F2"
                                            href={`${basePath}/${featured.slug}`}
                                        />
                                    </m.div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* GRID KART */}
                    {cards.length > 0 && (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {cards.map((card) => {
                                const isEven = card.index % 2 === 0;
                                const bgClass = isEven ? "bg-primary-light" : "bg-secondary";
                                const textClass = isEven ? "text-neutral-black" : "text-primary";

                                return (
                                    <m.article
                                        key={card.key}
                                        variants={slideUp}
                                        custom={breadcrumbs && breadcrumbs.length > 0 ? card.index + 4 : card.index + 2}
                                        className={clsx("flex flex-col overflow-hidden rounded-[40px] pb-6", bgClass, textClass)}
                                    >
                                        {/* ZAMIANA m.a na Link + m.div */}
                                        <Link
                                            href={`${basePath}/${card.slug}`}
                                            scroll={true}
                                            className="group block overflow-hidden p-2"
                                        >
                                            <m.div variants={reveal} className="block size-full h-full rounded-[32px] overflow-hidden">
                                                {card.image?.src && (
                                                    <Image
                                                        src={card.image.src}
                                                        alt={card.name || ""}
                                                        width={800}
                                                        height={600}
                                                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                                        loading="lazy"
                                                        decoding="async"
                                                        placeholder={card.image.blur ? "blur" : "empty"}
                                                        blurDataURL={card.image.blur}
                                                        className="aspect-[4/3] size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.015]"
                                                    />
                                                )}
                                            </m.div>
                                        </Link>

                                        <div className="flex flex-1 flex-col justify-between px-5 pt-4 pr-6 md:px-6 md:pt-8 md:pr-7">
                                            <div>
                                                <h3 className="mb-4 text-md font-medium md:text-2xl">{card.name}</h3>
                                                <p className="">{card.description}</p>
                                            </div>

                                            <div className="mt-8 self-end mr-4">
                                                <TextLinkButton
                                                    text="Pokaż szczegóły"
                                                    hoverColor="#803320"
                                                    primaryColor={isEven ? "#111" : "#FAF6F2"}
                                                    href={`${basePath}/${card.slug}`}
                                                />
                                            </div>
                                        </div>
                                    </m.article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </m.section>
        </LazyMotion>
    );
}